import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { validatePostalCode } from '@/lib/validators';

interface ParsedContract {
  row: number;
  // Property fields
  address: string;
  postalCode: string;
  city: string;
  // Tenant fields
  companyName: string;
  email: string;
  // Contract fields
  contractRef: string;
  contractDate: string;
  status: string;
  periodicity: string;
  charges: number;
  rentHT: number;
  totalTTC: number;
  // Validation
  errors: string[];
  valid: boolean;
  hasContract: boolean;
}

const TEMPLATE_HEADERS = [
  'Référence',
  'Type',
  'Adresse',
  'Étage',
  'Code postal',
  'Ville',
  'Surface',
  'Coût acquisition',
  'Notes',
  'Raison sociale',
  'Pays',
  'Email',
  'Contrat',
  'Date contrat',
  'Statut',
  'Périodicité',
  'Quantité',
  'Charge',
  'Loyer HT',
  'Montant TTC',
  'Détails',
];

function parseEuroAmount(value: string): number {
  if (!value) return 0;
  // Remove euro symbol and trim
  let cleaned = value.replace(/€/g, '').trim();
  // French format uses space or comma as thousands separator
  // Remove all spaces (thousands separator)
  cleaned = cleaned.replace(/\s/g, '');
  // If there's a comma followed by 3 digits at the end, it's a thousands separator
  // If there's a comma followed by 1-2 digits at the end, it's a decimal separator
  // Examples: "4,700" = 4700, "4,70" = 4.70
  if (/,\d{3}$/.test(cleaned)) {
    // Comma is thousands separator, just remove it
    cleaned = cleaned.replace(/,/g, '');
  } else {
    // Comma is decimal separator, replace with dot
    cleaned = cleaned.replace(',', '.');
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

function parseFrenchDate(dateStr: string): string | null {
  if (!dateStr) return null;
  // Parse DD/MM/YYYY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return null;
}

export function ImportContractsDialog() {
  const [open, setOpen] = useState(false);
  const [selectedSciId, setSelectedSciId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedContract[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase.from('scis').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const downloadTemplate = () => {
    const csvContent = [
      TEMPLATE_HEADERS.join(','),
      ',,14 rue du Poteau,,77181,Courtry,,,,EXAMPLE COMPANY,France,email@example.com,CRT00001,01/02/2026,Brouillon,Mensuelle,1,100 €,"1,500 €","1,920 €",',
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_contrats.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): ParsedContract[] => {
    // Remove BOM if present
    const cleanText = text.replace(/^\ufeff/, '');
    const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    // Auto-detect delimiter from header line
    const headerLine = lines[0];
    const delimiter = (headerLine.split(';').length > headerLine.split(',').length) ? ';' : ',';

    const dataLines = lines.slice(1);
    
    return dataLines.map((line, index) => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const errors: string[] = [];
      
      // Extract values (columns based on template)
      const address = values[2] || '';
      const postalCode = values[4] || '';
      const city = values[5] || '';
      const companyName = values[9] || '';
      const email = values[11] || '';
      const contractRef = values[12] || '';
      const contractDate = values[13] || '';
      const status = values[14] || '';
      const periodicity = values[15] || '';
      const charges = parseEuroAmount(values[17] || '0');
      const rentHT = parseEuroAmount(values[18] || '0');
      const totalTTC = parseEuroAmount(values[19] || '0');

      // Validate required fields
      if (!address) errors.push('Adresse requise');
      if (!postalCode) errors.push('Code postal requis');
      else {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) errors.push(postalValidation.message || 'Code postal invalide');
      }
      if (!city) errors.push('Ville requise');
      if (!companyName) errors.push('Raison sociale requise');

      const hasContract = !!contractRef && !!contractDate && rentHT > 0;

      return {
        row: index + 2,
        address,
        postalCode,
        city,
        companyName,
        email,
        contractRef,
        contractDate,
        status,
        periodicity,
        charges,
        rentHT,
        totalTTC,
        errors,
        valid: errors.length === 0,
        hasContract,
      };
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      setParsedData(parsed);
    };
    reader.readAsText(file, 'utf-8');
  };

  const importData = useMutation({
    mutationFn: async () => {
      if (!selectedSciId) throw new Error('Veuillez sélectionner une SCI');
      if (!selectedClientId) throw new Error('Veuillez sélectionner un client');

      const validData = parsedData.filter(d => d.valid);
      if (validData.length === 0) throw new Error('Aucune donnée valide à importer');

      // Get organization ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('Organisation non trouvée');

      const organizationId = profile.organization_id;

      // Group by address to create/find properties
      const addressGroups = new Map<string, ParsedContract[]>();
      validData.forEach(d => {
        const key = `${d.address}|${d.postalCode}|${d.city}`;
        if (!addressGroups.has(key)) {
          addressGroups.set(key, []);
        }
        addressGroups.get(key)!.push(d);
      });

      let createdContracts = 0;
      let createdTenants = 0;
      let createdProperties = 0;

      for (const [addressKey, contracts] of addressGroups) {
        const [address, postalCode, city] = addressKey.split('|');

        // Check if property exists
        let { data: existingProperty } = await supabase
          .from('properties')
          .select('id')
          .eq('address', address)
          .eq('postal_code', postalCode)
          .eq('sci_id', selectedSciId)
          .single();

        let propertyId: string;

        if (existingProperty) {
          propertyId = existingProperty.id;
        } else {
          // Create property
          const { data: newProperty, error: propError } = await supabase
            .from('properties')
            .insert({
              organization_id: organizationId,
              sci_id: selectedSciId,
              address,
              postal_code: postalCode,
              city,
              name: address,
            })
            .select('id')
            .single();

          if (propError) throw new Error(`Erreur création bien: ${propError.message}`);
          propertyId = newProperty.id;
          createdProperties++;
        }

        // Create tenants and contracts for this property
        for (const contract of contracts) {
          // Check if tenant exists by company name
          let { data: existingTenant } = await supabase
            .from('tenants')
            .select('id')
            .eq('company_name', contract.companyName)
            .eq('organization_id', organizationId)
            .single();

          let tenantId: string;

          if (existingTenant) {
            tenantId = existingTenant.id;
          } else {
            // Create tenant
            const { data: newTenant, error: tenantError } = await supabase
              .from('tenants')
              .insert({
                organization_id: organizationId,
                client_id: selectedClientId,
                tenant_type: 'professional',
                company_name: contract.companyName,
                email: contract.email || null,
                first_name: contract.companyName.split(' ')[0],
                last_name: contract.companyName.split(' ').slice(1).join(' ') || '-',
              })
              .select('id')
              .single();

            if (tenantError) throw new Error(`Erreur création locataire: ${tenantError.message}`);
            tenantId = newTenant.id;
            createdTenants++;
          }

          // Create contract only if it has contract info
          if (contract.hasContract) {
            const startDate = parseFrenchDate(contract.contractDate) || new Date().toISOString().split('T')[0];
            
            // Calculate VAT rate from TTC and HT
            const totalHT = contract.rentHT + contract.charges;
            const vatRate = totalHT > 0 ? Math.round(((contract.totalTTC / totalHT) - 1) * 100) : 0;

            const { error: leaseError } = await supabase
              .from('leases')
              .insert({
                organization_id: organizationId,
                property_id: propertyId,
                tenant_id: tenantId,
                start_date: startDate,
                rent_amount: contract.rentHT,
                charges_amount: contract.charges,
                vat_rate: vatRate > 0 ? vatRate : 0,
                payment_day: 5,
                status: 'active',
                auto_generate: true,
              });

            if (leaseError) throw new Error(`Erreur création contrat: ${leaseError.message}`);
            createdContracts++;
          }
        }
      }

      return { createdProperties, createdTenants, createdContracts };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(
        `Import réussi: ${result.createdProperties} bien(s), ${result.createdTenants} locataire(s), ${result.createdContracts} contrat(s) créé(s)`
      );
      setOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de l\'import');
    },
  });

  const resetForm = () => {
    setParsedData([]);
    setFileName('');
    setSelectedSciId('');
    setSelectedClientId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter(d => d.valid).length;
  const contractCount = parsedData.filter(d => d.valid && d.hasContract).length;
  const invalidCount = parsedData.filter(d => !d.valid).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="mr-2 h-4 w-4" /> Importer CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Importer des contrats depuis CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Step 1: Select SCI and Client */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SCI *</Label>
              <Select value={selectedSciId} onValueChange={setSelectedSciId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une SCI" />
                </SelectTrigger>
                <SelectContent>
                  {scis.map((sci) => (
                    <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Step 2: Upload file */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={downloadTemplate} size="sm">
              <Download className="mr-2 h-4 w-4" /> Télécharger le modèle
            </Button>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-import"
              />
              <label htmlFor="csv-import">
                <Button variant="outline" asChild>
                  <span>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    {fileName || 'Choisir un fichier CSV'}
                  </span>
                </Button>
              </label>
            </div>
          </div>

          {/* Preview */}
          {parsedData.length > 0 && (
            <>
              <div className="flex items-center gap-4">
                <Badge variant="secondary">
                  <Check className="mr-1 h-3 w-3" /> {validCount} valide(s)
                </Badge>
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {contractCount} contrat(s) à créer
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="destructive">
                    <X className="mr-1 h-3 w-3" /> {invalidCount} invalide(s)
                  </Badge>
                )}
              </div>

              <ScrollArea className="flex-1 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Locataire</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Loyer HT</TableHead>
                      <TableHead>Charges</TableHead>
                      <TableHead>Contrat</TableHead>
                      <TableHead className="w-20">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.map((row) => (
                      <TableRow key={row.row} className={!row.valid ? 'bg-red-50' : ''}>
                        <TableCell className="font-mono text-xs">{row.row}</TableCell>
                        <TableCell>
                          <div className="text-sm">{row.address}</div>
                          <div className="text-xs text-muted-foreground">{row.postalCode} {row.city}</div>
                        </TableCell>
                        <TableCell className="font-medium">{row.companyName}</TableCell>
                        <TableCell className="text-xs">{row.email || '-'}</TableCell>
                        <TableCell>{row.rentHT.toLocaleString('fr-FR')} €</TableCell>
                        <TableCell>{row.charges.toLocaleString('fr-FR')} €</TableCell>
                        <TableCell>
                          {row.hasContract ? (
                            <Badge variant="outline" className="text-xs">
                              {row.contractRef}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.valid ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <div className="flex items-center gap-1">
                              <AlertCircle className="h-4 w-4 text-red-500" />
                              <span className="text-xs text-red-600">{row.errors[0]}</span>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              {(!selectedSciId || !selectedClientId) && (
                <p className="text-sm text-amber-600 text-center">
                  ⚠️ Veuillez sélectionner une SCI et un Client pour activer l'import
                </p>
              )}

              <Button 
                onClick={() => importData.mutate()} 
                disabled={importData.isPending || validCount === 0 || !selectedSciId || !selectedClientId}
                className="w-full"
              >
                {importData.isPending ? 'Import en cours...' : `Importer ${contractCount} contrat(s)`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
